import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import { getCampaignPerformanceById } from "../../actions/campaign-performance/CampaignPerformanceActions";
import { getCampaignPlanOverview } from "../../../actions/campaign-plan/CampaignPlanActions";
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { getCampaignMedia } from "../../../actions/campaign-media/CampaignMediaActions";

// Utils and Constants
import { getDifferenceInDays } from "../../../common-utils/date-utils/DateUtils";
import { defaultPagination } from "../../../constants/UrlConstants";
import { getCitySpecificMediaList } from "../media-selection/MediaContainerUtil";
import { getUpdatedCityName } from "../../../common-utils/city-utils/CityUtils";

// Components
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import Spinner from "../../../components/spinner/Spinner";
import TableHeaders from "../../../components/table/TableHeaders";
import { isBuyerUserSpecific } from "../../../components/hoc/MediaAgencySpecific";
import {
  CampaignDetails,
  CancelCampaign,
  DownloadCampaignExcelReport,
  StopCampaign,
} from "../components/HeaderNav";
import { ProgressBarLarge } from "../components/ProgressBarLarge";
import CampaignViewCards from "../components/CampaignViewCards";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";

// Pages and Section
import CityDetailsTable, {
  getEstImpForTgAndGeneric,
} from "../components/CityDetailsTable";

// Page Constants
function getLiveCityPerformanceTableHeaders(
  activeCities,
  totalCities,
  campaignDuration,
  campaignDaysCompleted,
  totalActiveSitesCount,
  totalMediaCount
) {
  return [
    {
      title: {
        displayName: (
          <span>
            {"Active Cities: "}
            <b>{`${activeCities} / ${totalCities}`}</b>
          </span>
        ),
        className: "col-3 border-right",
      },
    },
    {
      title: {
        displayName: (
          <div className="d-flex justify-content-between">
            <span>
              {"Active Media Sites: "}
              <b>{`${totalActiveSitesCount} / ${totalMediaCount || 0}`}</b>
            </span>
            <span>
              <b>{campaignDaysCompleted || 0}</b>
              {" of the total "}
              <b>{campaignDuration || 0}</b>
              {" days Completed"}
            </span>
          </div>
        ),
        className: "col-9",
      },
    },
  ];
}

// Page-Component
function LiveOverviewHeader({ campaign, campaignPlanOverview }) {
  // selector state
  const updateStateLoading = useSelector(
    (state) => state.c_campaignBasicInfo.updateStateLoading
  );

  if (!campaign) {
    return null;
  }

  const { id: campaignId } = campaign || {};

  const EnableCancelCampaign = isBuyerUserSpecific(CancelCampaign);
  const EnableStopCampaign = isBuyerUserSpecific(StopCampaign);

  const pageTitle = <CampaignDetails campaign={campaign} />;
  const pageActions = (
    <div className="d-flex align-items-center">
      {EnableCancelCampaign && <EnableCancelCampaign campaignId={campaignId} />}
      {updateStateLoading && <Spinner className="mr-3" />}
      {EnableStopCampaign && <EnableStopCampaign campaignId={campaignId} />}
      <DownloadCampaignExcelReport
        campaign={campaign}
        campaignPlanOverview={campaignPlanOverview}
      />
    </div>
  );

  return <PageHeader title={pageTitle} actions={pageActions} shadow={true} />;
}

function LiveCityPerformanceTableRow({
  cityId,
  eachLogSummary = {},
  infoByCity = {},
  eachCityInfo = {},
  cityMedias = [],
}) {
  const { targetGroupId } = eachCityInfo || {};

  const { targetOtsServedTotal, otsServedTotal } = eachLogSummary || {};
  const { cityName, mediaCount, activeMediaCount } = infoByCity[cityId] || {};
  const updatedCityName = getUpdatedCityName(cityId, cityName);

  // calculating total impression base on stretch specific medias
  const { genericImp: plannedGenericOts, targetImp: plannedTargetOts } =
    getEstImpForTgAndGeneric(eachCityInfo, cityMedias);

  return (
    <tr>
      <td className="border-right">
        <div className="text-right">
          <p
            className="mb-1 text-primary text-truncate"
            title={updatedCityName}
          >
            {updatedCityName}
          </p>
          {targetGroupId && <span className="text-muted mb-1">{"TG"}</span>}
          <span className="text-muted d-block mt-1">{"General"}</span>
        </div>
      </td>
      <td>
        <div className="text-left">
          <p className="mb-1">
            <b>{activeMediaCount}</b> of <b>{mediaCount}</b> sites are Active
          </p>

          {/* Tg Impression */}
          {targetGroupId && (
            <ProgressBarLarge
              impression={targetOtsServedTotal}
              plannedEstImpressions={plannedTargetOts}
              className="bg-success"
            />
          )}
          {/* General Impression */}
          <ProgressBarLarge
            impression={otsServedTotal}
            plannedEstImpressions={plannedGenericOts}
            className="bg-primary"
          />
        </div>
      </td>
    </tr>
  );
}

function getTotalAndActiveCitiesCount(planByCity) {
  const planCityIds = Object.keys(planByCity || {});

  // if no city present
  if (planCityIds.length === 0) {
    return { totalCities: 0, activeCities: 0 };
  }

  // total
  const totalCities = planCityIds.length;

  // active
  const activeCities = Object.values(planByCity).reduce((acc, eachCity) => {
    if (eachCity.isLive) {
      return acc + 1;
    }
    return acc;
  }, 0);

  return { totalCities, activeCities };
}

function LiveCityPerformanceTable({
  campaignPlanOverview = {},
  campaign = {},
  campaignPerformance = {},
}) {
  // performance loading
  const campaignPerformanceLoading = useSelector(
    (state) => state.campaignPerformance.campaignPerformanceLoading
  );

  // campaign medias
  const campaignMedias = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // plan overview data
  const { planByCity = {} } = campaignPlanOverview || {};
  const {
    infoByCity = {},
    mediaCount,
    startTimestamp,
    endTimestamp,
  } = campaign || {};

  const totalActiveSitesCount = Object.values(infoByCity).reduce(
    (acc, eachCity) => acc + (eachCity?.activeMediaCount || 0),
    0
  );

  const campaignDuration = getDifferenceInDays(startTimestamp, endTimestamp);

  const todayDate = new Date();
  const campaignDaysCompleted = getDifferenceInDays(startTimestamp, todayDate);

  // get total and active cities
  const { totalCities, activeCities } =
    getTotalAndActiveCitiesCount(planByCity);

  // Table headers
  const tableHeaders = getLiveCityPerformanceTableHeaders(
    activeCities,
    totalCities,
    campaignDuration,
    campaignDaysCompleted - 1,
    totalActiveSitesCount,
    mediaCount
  );

  // performance loading
  if (campaignPerformanceLoading) {
    return (
      <AlertMessage
        className="justify-content-center p-3 my-4"
        textMessage="Campaign performance is loading, Please wait..."
      />
    );
  }

  return (
    <div className="mt-5">
      <h4>Performance</h4>
      <div className="table-responsive">
        <table className="table table-fixed">
          <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
          <tbody>
            {Object.keys(planByCity).map((eachCityId) => {
              const citySpecificMediaList = getCitySpecificMediaList(
                campaignMedias,
                eachCityId
              );
              return (
                <LiveCityPerformanceTableRow
                  key={eachCityId}
                  eachLogSummary={campaignPerformance[eachCityId]}
                  cityId={eachCityId}
                  infoByCity={infoByCity}
                  eachCityInfo={planByCity[eachCityId]}
                  cityMedias={citySpecificMediaList}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Page
 */
export default function CampaignLiveOverviewPage() {
  const dispatch = useDispatch();
  const { campaignId } = useParams();

  // Get campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  // Get campaignPlan
  const campaignPlanOverview = useSelector(
    (state) => state.campaignPlan.campaignPlanOverview
  );
  const { planByCity = {} } = campaignPlanOverview || {};

  // Selector
  const campaignPerformance =
    useSelector((state) => state.campaignPerformance.campaignPerformance) || {};

  // Loadings
  const campaignPlanOverviewLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanOverviewLoading
  );
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const campaignMediasLoading = useSelector(
    (state) => state.campaignMedia.campaignMediaLoading
  );

  const isPageLoading =
    campaignPlanOverviewLoading ||
    basicCampaignDetailsLoading ||
    campaignMediasLoading;

  const { pageNo, pageSize } = defaultPagination;
  useEffect(() => {
    dispatch(getCampaignPlanOverview(campaignId));
    dispatch(getBasicCampaignDetails(campaignId));
    dispatch(getCampaignPerformanceById(campaignId));

    // campaign medias
    dispatch(getCampaignMedia(campaignId, pageNo, pageSize));
  }, [dispatch, campaignId]);

  // Sidebar Settings
  const redirectLinkObj = {
    campaignOverviewLink: `/campaign/${campaignId}/live`,
    citySummaryLink: `/campaign/${campaignId}/city/:cityId/live`,
  };

  // page loading
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar campaign={campaign} redirectLinkObj={redirectLinkObj} />

      {/* Content-Section */}
      <div className="content-wrapper px-0">
        <LiveOverviewHeader
          campaign={campaign}
          campaignPlanOverview={campaignPlanOverview}
        />

        {/* Page-Content */}
        <div className="page-content">
          <CampaignViewCards
            campaign={campaign}
            campaignPlanOverview={campaignPlanOverview}
            mediaCount={campaign.mediaCount}
          />

          {/* Campaign Cities Performance */}
          <LiveCityPerformanceTable
            campaignPlanOverview={campaignPlanOverview}
            campaign={campaign}
            campaignPerformance={campaignPerformance}
          />

          {/* City Details Table */}
          <CityDetailsTable
            planByCity={planByCity}
            campaign={campaign}
            campaignPerformance={campaignPerformance}
          />
        </div>
      </div>
    </>
  );
}
